/* eslint-disable max-len */
import {hot} from 'react-hot-loader/root';

import React, {useState, useEffect, Suspense, lazy} from 'react';
import {useTranslation} from 'react-i18next';
import {Switch, Route, Redirect, useHistory} from 'react-router-dom';
import {useSelector, useDispatch} from 'react-redux';
import {Helmet} from 'react-helmet';

import styled, {css} from 'styled-components';
import {GlobalStyle, useIsWeb} from './styles';

import * as UserActions from './store/actions/user';
import {resetRecommendations} from './store/actions/recommendation';

import AppHeader from './components/AppHeader';
import Footer from './components/Footer';
import Toast from './components/Toast';
import Modal from './components/Modal';
import Loader from './components/Loader';
import DownloadApp from './components/DownloadApp';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';

import {getCurrentLocale} from './utils/i18n';
import BottomNav from './components/BottomNav';
import SmartBanner from './components/SmartBanner';
import {isMobileDevice, useBrand} from './helpers';

import DrawerMenu from './components/DrawerMenu';

import {ErrorContainer} from './components/ErrorContainer';
import {installOneTrustScript} from './utils/onetrust';

import {interactedWithApp} from './store/actions/video';
import Search from './pages/Search';

import {selectDKidsEnabled} from './store/reducers';
import {selectYouboraCode} from './store/selectors/config';

const Home = lazy(() => import('./pages/Home'));
const OnNow = lazy(() => import('./pages/OnNow'));
const Shows = lazy(() => import('./pages/Shows'));
const ShowDetails = lazy(() => import('./pages/ShowDetails'));
const Games = lazy(() => import('./pages/Games'));
const Unsupported = lazy(() => import('./pages/Unsupported'));
const Cartoonito = lazy(() => import('./pages/Cartoonito'));
const Favourites = lazy(() => import('./pages/Favourites'));
const Movies = lazy(() => import('./pages/Movies'));
const LivePlayer = lazy(() => import('./pages/LivePlayer'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Comics = lazy(() => import('./pages/Comics'));
const ComicsDetails = lazy(() => import('./pages/ComicsDetails'));
const ComicsChapterReader = lazy(() => import('./pages/ComicsChapterReader'));

const Player = lazy(() => import('./pages/Player'));
const CartoonitoPlayer = lazy(() => import('./pages/CartoonitoPlayer'));
const CartoonitoPremiere = lazy(() => import('./pages/CartoonitoPremiere'));
const CartoonitoHome = lazy(() => import('./pages/CartoonitoHome'));
const Cartoons = lazy(() => import('./pages/Cartoons'));
const CartoonitoParentalArea = lazy(() =>
    import('./pages/CartoonitoParentalArea'),
);
const CartoonitoLive = lazy(() => import('./pages/CartoonitoLive'));

const CartoonDetails = lazy(() => import('./pages/CartoonDetails'));
const CartoonitoGames = lazy(() => import('./pages/CartoonitoGames'));

const DKidsHome = lazy(() => import('./pages/DKidsHome'));
const DKidsGameDetails = lazy(() => import('./pages/DKidsGameDetails'));
const DKidsPolicy = lazy(() => import('./pages/DKidsPolicy'));
const Programming = lazy(() => import('./pages/Programming'));

var youbora = require('youboralib');

const Container = styled.div`
    padding-top: ${props => props.paddingTop}px;
    margin: 0px;
    min-height: calc(100vh - 392px);
    display: flex;
    flex-direction: column;
    width: 100%;

    ${({hasFixedHeader}) =>
        hasFixedHeader &&
        css`
            padding-top: 0px;
        `}
`;

const LoadingContainer = styled.div`
position: ${props => props.position || 'relative'}
    top: 0px;
    bottom: 0px;
    left: 0px;
    right: 0px;
    background-color: #080008;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 500px;
    flex: 1;
`;

const App = props => {
    const history = useHistory();
    const dispatch = useDispatch();

    let app_env = process.env.REACT_APP_STAGE || 'dev';
    app_env = app_env === 'prod' ? 'prd' : app_env;

    const toast = useSelector(state => state.ui.toast);
    const modal = {...useSelector(state => state.ui.modal)};
    const user = useSelector(state => state.user);
    const brand = useBrand();
    const isCartoonito = brand === 'cnito';
    const isDKids = brand === 'dkids';
    const isWeb = useIsWeb();
    const isDKidsEnabled = useSelector(selectDKidsEnabled);
    const youboraAccount = useSelector(selectYouboraCode);

    //need to rework this logic
    const parts = history.location.pathname.split('/');
    parts.shift(); //don't care about first item, as its a blank string

    const hideFooter =
        (parts[0] === 'comics' && parts.length === 3) ||
        (parts[0] === 'now' && parts.length > 1) ||
        (parts[0] === 'live' && parts.length > 1);
    const isLive = parts[0] === 'now';

    const [showSmartBanner, setShowSmartBanner] = useState(
        (isDKids && isDKidsEnabled) || brand === 'cnapp',
    );

    const [currentPathname, setCurrentPathname] = useState(
        window.location.pathname,
    );
    const [tokenRefreshed, setTokenRefreshed] = useState(false);
    const [userCountryInfo, setUserCountryInfo] = useState(undefined);
    const [authenticated, setAuthenticated] = useState(false);
    const [showDrawerMenu, setShowDrawerMenu] = useState(false);

    useEffect(() => {
        if (youboraAccount) {
            window.plugin = new youbora.Plugin({accountCode: youboraAccount});
        }
    }, [youboraAccount]);

    useEffect(() => {
        let {oneTrustScript, optanonScript} = installOneTrustScript();

        window.addEventListener('orientationchange', () => {
            window.scrollTo({top: 0});
        });

        history.listen(location => {
            window.scrollTo({top: 0});

            setCurrentPathname(location.pathname);
        });

        fetch(`https://appconfigs.${app_env}.wmgp.io/cnplus/web/config.json`)
            .then(res => res.json())
            .then(res => {
                res['btw'] = {
                    ...(res['btw'] || {}),
                    enabled: true,
                };

                let userAgent = navigator.userAgent.toLocaleLowerCase();

                // safari verification
                if (/safari/.test(userAgent) && !/chrome/.test(userAgent)) {
                    res.user_device = 'IPHONE';
                    res.catalog_settings.platforms = ['IPHONE'];
                } else {
                    res.user_device = 'DEFAULT';
                    res.catalog_settings.platforms = ['PCTV_DASH'];
                }

                dispatch({type: 'CONFIG', payload: res});
            });

        return () => {
            oneTrustScript && document.head.removeChild(oneTrustScript);
            optanonScript && document.head.removeChild(optanonScript);
        };

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        //reset user if he had a jwtToken - we disabled login for now
        if (user && user.jwtToken) {
            dispatch(UserActions.logout());
        }
    }, [user, dispatch]);

    useEffect(() => {
        const setupLocation = async () => {
            let location_response = undefined;

            if (userCountryInfo === undefined) {
                location_response = await fetch(
                    // eslint-disable-next-line
                    `https://avs-proxy.${app_env}.aaa.wmgp.io/v1/location/CNPLUSTVE`,
                )
                    .then(res => res.json())
                    .then(res => res)
                    .catch(err => err);

                setUserCountryInfo(location_response);
            }

            let catalogLanguage = getCurrentLocale();

            dispatch(
                UserActions.login({
                    ...(location_response &&
                        location_response.detected &&
                        location_response.detected[0]),
                    catalogLanguage,
                }),
            );
        };

        setupLocation();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //autoplay interaction
    useEffect(() => {
        const onInteract = () => {
            dispatch(interactedWithApp());
        };
        window.addEventListener('click', onInteract);
        return () => {
            window.removeEventListener('click', onInteract);
        };
    }, [dispatch]);

    const handleLogout = () => {
        setAuthenticated(false);
        if (
            Object.keys(user).length > 6 ||
            (user.token !== true && user.token !== undefined)
        ) {
            dispatch(resetRecommendations());
            dispatch(UserActions.logout());
        }
    };

    const isParentsArea = ['/pais', '/parents/', '/padres'].includes(
        currentPathname,
    );

    const isCNitoUrl = () => {
        const cnitoUrls = [
            'cartoonito.cl',
            'cartoonito.com.ar',
            'cartoonito.com.co',
            'cartoonito.com.ec',
            'cartoonito.com.mx',
            'cartoonito.com.pe',
            'cartoonito.com.uy',
            'cartoonito.com.ve',
            'cartoonito.com.br',
            'cartoonitola.com',
        ];

        return cnitoUrls.includes(
            window.location.origin.replace(/(www.|https:\/\/|http:\/\/)+/g, ''),
        );
    };

    const addCanonicalTag = () => {
        if (isCNitoUrl())
            return <link rel="canonical" href={window.location.origin} />;

        return null;
    };

    const setupFavicon = () => {
        if (isCNitoUrl()) {
            return (
                <link
                    rel="icon"
                    class="js-site-favicon"
                    type="image/svg+xml"
                    href="/favicon-cnito.svg"
                />
            );
        } else if (isDKids) {
            return (
                <link rel="icon" type="image/svg+xml" href="/favicon-dk.svg" />
            );
        }
        return <link rel="icon" href="/favicon.ico" />;
    };

    const openDrawer = open => {
        setShowDrawerMenu(open);
        dispatch(UserActions.hideBadge());
    };

    return (
        <>
            <Helmet>
                {addCanonicalTag()}

                {isMobileDevice() && (
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
                    />
                )}
                {setupFavicon()}
            </Helmet>

            {window.location.search.indexOf('?code=') >= 0 ||
            (authenticated ? !user.jwtToken || !user.idp : false) ? (
                <LoadingContainer position="absolute">
                    <Loader dark />
                </LoadingContainer>
            ) : (
                <Switch>
                    <Route exact path="/download-app">
                        <DownloadApp />
                    </Route>

                    <Route exact path="/unsupported">
                        <Unsupported />
                    </Route>

                    <Route path="*">
                        <GlobalStyle />

                        {showSmartBanner && (
                            <SmartBanner
                                close={() => setShowSmartBanner(false)}
                            />
                        )}

                        <AppHeader
                            setShowDrawerMenu={openDrawer}
                            currentPathname={currentPathname}
                            paddingTop={showSmartBanner && !isWeb ? 68 : 0}
                        />

                        <BottomNav currentPathname={currentPathname} />

                        <DrawerMenu
                            defaultState={showDrawerMenu}
                            callback={open => setShowDrawerMenu(open)}
                        />

                        <Container
                            hasFixedHeader={false}
                            paddingTop={
                                showSmartBanner && !isWeb
                                    ? hideFooter
                                        ? 68
                                        : 68
                                    : (isCartoonito ||
                                          isDKids ||
                                          hideFooter ||
                                          isLive) &&
                                      !isParentsArea
                                    ? 0
                                    : 80
                            }
                        >
                            <Suspense
                                fallback={
                                    <LoadingContainer>
                                        <Loader />
                                    </LoadingContainer>
                                }
                            >
                                <ErrorBoundary>
                                    <Switch>
                                        <Route exact path="/error">
                                            <ErrorContainer />
                                        </Route>

                                        {isCartoonito
                                            ? [
                                                  <Route
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/landing',
                                                          '/programacao',
                                                          '/programacion',
                                                      ]}
                                                  >
                                                      <Cartoonito />
                                                  </Route>,
                                                  <Route
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/evento"
                                                  >
                                                      <CartoonitoPremiere />
                                                  </Route>,

                                                  <Route
                                                      key="onboarding"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/onboarding"
                                                  >
                                                      <Onboarding />
                                                  </Route>,
                                                  <Route
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/"
                                                  >
                                                      <CartoonitoHome />
                                                  </Route>,
                                                  <Route
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/cartoons"
                                                  >
                                                      <Cartoons />
                                                  </Route>,
                                                  <Route
                                                      key="show"
                                                      exact
                                                      path="/cartoon/:show"
                                                      render={props => {
                                                          setCurrentPathname(
                                                              props.location
                                                                  .pathname,
                                                          );
                                                          return (
                                                              <CartoonDetails />
                                                          );
                                                      }}
                                                  />,
                                                  <Route
                                                      key="play"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/play/:episode_id?"
                                                  >
                                                      <Suspense
                                                          fallback={<Loader />}
                                                      >
                                                          <CartoonitoPlayer />
                                                      </Suspense>
                                                  </Route>,
                                                  <Route
                                                      key="games"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/games',
                                                          '/games/play/:game_id',
                                                      ]}
                                                  >
                                                      <CartoonitoGames />
                                                  </Route>,

                                                  <Route
                                                      key="live"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={['/live']}
                                                  >
                                                      <CartoonitoLive />
                                                  </Route>,
                                                  <Route
                                                      key="livePlayer"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/live/:live_id"
                                                  >
                                                      <LivePlayer />
                                                  </Route>,

                                                  <Route
                                                      key="parentalArea"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/parents',
                                                          '/padres',
                                                          '/pais',
                                                      ]}
                                                  >
                                                      <CartoonitoParentalArea />
                                                  </Route>,
                                              ]
                                            : isDKids
                                            ? [
                                                  <Route
                                                      key="home"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={['/', '/home']}
                                                  >
                                                      <DKidsHome />
                                                  </Route>,

                                                  <Route
                                                      key="game"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/game/:slug',
                                                          '/jogos/:slug',
                                                          '/juegos/:slug',
                                                      ]}
                                                  >
                                                      <DKidsGameDetails />
                                                  </Route>,
                                                  <Route
                                                      key="livePlayer"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/now/:live_id"
                                                  >
                                                      <LivePlayer />
                                                  </Route>,
                                                  <Route
                                                      key="policy"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/politica-de-privacidade',
                                                          '/politica-de-privacidad',
                                                          '/termos-de-uso',
                                                          '/terminos-de-uso',
                                                      ]}
                                                  >
                                                      <DKidsPolicy />
                                                  </Route>,
                                                  <Route
                                                      key="error"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="*"
                                                  >
                                                      <ErrorContainer />
                                                  </Route>,
                                              ]
                                            : [
                                                  <Route
                                                      key="home"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={['/', '/home']}
                                                  >
                                                      <Home />
                                                  </Route>,

                                                  <Route
                                                      key="onboarding"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/onboarding"
                                                  >
                                                      <Onboarding />
                                                  </Route>,

                                                  <Route
                                                      key="show"
                                                      exact
                                                      path={[
                                                          '/shows/:show',
                                                          '/shows/:show/season/:season',
                                                      ]}
                                                      render={props => {
                                                          setCurrentPathname(
                                                              props.location
                                                                  .pathname,
                                                          );
                                                          return (
                                                              <ShowDetails />
                                                          );
                                                      }}
                                                  />,

                                                  <Route
                                                      key="shows"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/shows"
                                                  >
                                                      <Shows />
                                                  </Route>,

                                                  <Route
                                                      key="movies"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/movies"
                                                  >
                                                      <Movies />
                                                  </Route>,

                                                  <Route
                                                      key="games"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/games',
                                                          '/games/play/:game_id',
                                                      ]}
                                                  >
                                                      <Games />
                                                  </Route>,

                                                  <Route
                                                      key="comics"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={['/comics']}
                                                  >
                                                      <Comics />
                                                  </Route>,

                                                  <Route
                                                      key="comicsDetails"
                                                      exact
                                                      path={[
                                                          '/comics/:comic_id',
                                                      ]}
                                                      render={props => {
                                                          setCurrentPathname(
                                                              props.location
                                                                  .pathname,
                                                          );
                                                          return (
                                                              <ComicsDetails />
                                                          );
                                                      }}
                                                  />,
                                                  <Route
                                                      key="comicsReader"
                                                      exact
                                                      path={[
                                                          '/comics/:comic_id/:chapter',
                                                      ]}
                                                      render={props => {
                                                          setCurrentPathname(
                                                              props.location
                                                                  .pathname,
                                                          );
                                                          return (
                                                              <ComicsChapterReader
                                                                  key={
                                                                      props
                                                                          .match
                                                                          .params
                                                                          .chapter
                                                                  }
                                                              />
                                                          );
                                                      }}
                                                  />,

                                                  <PrivateRoute
                                                      key="favourites"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/favourites"
                                                  >
                                                      <Favourites />
                                                  </PrivateRoute>,

                                                  <Route
                                                      key="play"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/play/:episode_id?"
                                                  >
                                                      <Suspense
                                                          fallback={
                                                              <Loader
                                                                  dark
                                                                  label="Loading..."
                                                              />
                                                          }
                                                      >
                                                          <Player />
                                                      </Suspense>
                                                  </Route>,

                                                  <Route
                                                      key="now"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/now"
                                                  >
                                                      <OnNow />
                                                  </Route>,

                                                  <Route
                                                      key="livePlayer"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path="/now/:live_id"
                                                  >
                                                      <LivePlayer />
                                                  </Route>,
                                                  <Route
                                                      key="search"
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={['/search']}
                                                  >
                                                      <Search />
                                                  </Route>,
                                                  <Route
                                                      setCurrentPathname={
                                                          setCurrentPathname
                                                      }
                                                      exact
                                                      path={[
                                                          '/landing',
                                                          '/programacao',
                                                          '/programacion',
                                                      ]}
                                                  >
                                                      <Programming />
                                                  </Route>,
                                              ]}

                                        <Route
                                            path="*"
                                            render={({location}) => {
                                                return (
                                                    <Redirect
                                                        to={{
                                                            ...location,
                                                            pathname: '/',
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                    </Switch>
                                </ErrorBoundary>
                            </Suspense>
                        </Container>

                        <Toast active={toast.visible} value={toast.message} />

                        {!hideFooter && (
                            <Footer
                                paddingBottom={
                                    showSmartBanner && isWeb ? 100 : 0
                                }
                            />
                        )}

                        {modal.visible && (
                            <Modal
                                title={modal.title}
                                buttons={modal.buttons}
                                content={modal.content}
                                hideClose={modal.hideClose}
                            />
                        )}
                    </Route>
                </Switch>
            )}
        </>
    );
};

export default hot(App);
